import { Container, Graphics, RenderTexture, type Renderer, type Texture } from 'pixi.js';

/**
 * Procedurally generated art.
 *
 * No asset files: every shape here is drawn with the Graphics API and baked into
 * a RenderTexture at boot. A sprite sheet on the wire would be tens of kilobytes
 * a player has to download before the first round; this costs a few milliseconds
 * of GPU time once.
 *
 * The figure is built from curves rather than from rectangles and circles. A
 * boxer assembled out of primitives reads as a toy at any size, and the seams
 * between the primitives are exactly what the eye catches when the sprite is
 * animating. Every limb here is a tapered shape with a bezier along each side,
 * so an arm is thick at the shoulder and narrow at the wrist and its outline is
 * continuous.
 *
 * Replacing this with a drawn atlas later means changing only `buildAssets`:
 * load the atlas, return the same frame arrays, and nothing downstream moves.
 */

export interface GameAssets {
  /** One punch cycle per arm, concatenated. */
  boxerFrames: Texture[];
  /** Which frame of each cycle is the moment of impact. */
  impactFrames: number[];
  bag: Texture;
  /** A torn chunk of bag: canvas, leather, stuffing. */
  particle: Texture;
  /** A single grain of sand, soft-edged, for the plume. */
  sand: Texture;
  floor: Texture;
}

const FRAME_W = 240;
const FRAME_H = 320;

/**
 * Frames per punch, and where in the cycle the glove lands.
 *
 * Fourteen rather than eight: the old cycle had four frames of travel between
 * guard and full extension, which at any speed above a crawl skipped visibly.
 * The frames are not evenly spaced in *pose* -- see reachAt -- only in time, so
 * the extra ones land where the motion is fastest.
 */
const FRAMES_PER_ARM = 14;
const IMPACT_FRAME = 7;

const PALETTE = {
  skin: 0xd8a074,
  skinLit: 0xecb98d,
  skinShade: 0xa9764f,
  skinDeep: 0x8a5f3f,
  trunks: 0x18202e,
  trunksLit: 0x26314a,
  trunksTrim: 0xf0b429,
  glove: 0xd92b4b,
  gloveLit: 0xf25370,
  gloveShade: 0x8e1a30,
  boot: 0x11161f,
  bootLit: 0x1d2534,
  hair: 0x241a14,
  bagBody: 0x6b4a2f,
  bagShade: 0x402c1b,
  bagHighlight: 0x9a7148,
  bagCanvas: 0x7d5836,
  strap: 0x2c333f,
  strapLit: 0x445064,
  floor: 0x141a24,
  floorLine: 0x232c3a,
  sand: 0xe3cba5,
} as const;

export async function buildAssets(renderer: Renderer): Promise<GameAssets> {
  const boxerFrames: Texture[] = [];
  const impactFrames: number[] = [];

  for (const arm of ['lead', 'rear'] as const) {
    impactFrames.push(boxerFrames.length + IMPACT_FRAME);
    for (let i = 0; i < FRAMES_PER_ARM; i += 1) {
      const phase = i / FRAMES_PER_ARM;
      boxerFrames.push(bake(renderer, FRAME_W, FRAME_H, (g) => drawBoxer(g, phase, arm)));
    }
  }

  return {
    boxerFrames,
    impactFrames,
    bag: bake(renderer, 150, 300, drawBag),
    particle: bake(renderer, 18, 18, drawChunk),
    sand: bake(renderer, 12, 12, drawSandGrain),
    floor: bake(renderer, 512, 96, drawFloor),
  };
}

/** Renders a Graphics into a texture once, at boot. */
function bake(renderer: Renderer, width: number, height: number, draw: (g: Graphics) => void): Texture {
  const g = new Graphics();
  draw(g);

  const holder = new Container();
  holder.addChild(g);

  const target = RenderTexture.create({
    width,
    height,
    // Bake at the display's density so the shapes stay crisp on a retina screen.
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    antialias: true,
  });

  renderer.render({ container: holder, target, clear: true });
  g.destroy();
  return target;
}

// ---------------------------------------------------------------------------
// Easing
// ---------------------------------------------------------------------------

/*
 * A punch is not a linear interpolation between two poses. It cocks back
 * slowly, accelerates into the target, and snaps back fast before settling. The
 * curves below are what turn eight evenly spaced poses into something that
 * reads as weight -- the old cycle used hand-written constants and every
 * transition arrived at the same speed, which is what made it look mechanical.
 */

function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

function easeInCubic(t: number): number {
  return t * t * t;
}

function easeOutQuint(t: number): number {
  return 1 - Math.pow(1 - t, 5);
}

function easeOutBack(t: number): number {
  // Overshoots slightly, which on the recovery reads as the arm being pulled
  // back into the guard rather than drifting there.
  const c = 1.35;
  return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
}

/**
 * How far the throwing arm is extended at a point in the cycle.
 *
 * Returns -0.2 at the top of the wind-up (the fist drawn back behind the
 * shoulder) through 0 at the guard to 1 at full extension.
 */
function reachAt(phase: number): number {
  const COCK_END = 0.24;
  const IMPACT = IMPACT_FRAME / FRAMES_PER_ARM;

  if (phase < COCK_END) {
    // Wind up: unhurried, and the only part of the cycle that moves backwards.
    return -0.2 * easeInOutSine(phase / COCK_END);
  }
  if (phase < IMPACT) {
    // The drive. easeInCubic accelerates all the way into the impact frame,
    // which is what puts the speed where the eye expects it.
    const t = (phase - COCK_END) / (IMPACT - COCK_END);
    return -0.2 + 1.2 * easeInCubic(t);
  }
  // Recovery: the arm comes off the target fast and then settles, which is the
  // opposite curve to the drive and why the two do not look like one motion
  // played forwards and backwards.
  const t = (phase - IMPACT) / (1 - IMPACT);
  return 1 - easeOutQuint(t);
}

/** The guard hand's small counter-motion, opposite the throwing arm. */
function guardAt(phase: number): number {
  const IMPACT = IMPACT_FRAME / FRAMES_PER_ARM;
  if (phase < IMPACT) return 0.1 * easeInOutSine(phase / IMPACT);
  return 0.1 * (1 - easeOutBack(Math.min(1, (phase - IMPACT) / (1 - IMPACT))));
}

// ---------------------------------------------------------------------------
// The boxer
// ---------------------------------------------------------------------------

/**
 * Draws the boxer for one frame of the cycle.
 *
 * `phase` runs 0..1 through one punch. `arm` says which hand is throwing, so the
 * two cycles read as a one-two rather than the same jab twice: the rear hand is
 * the heavier shot, and it turns the hips further.
 */
function drawBoxer(g: Graphics, phase: number, arm: 'lead' | 'rear'): void {
  const reach = reachAt(phase);
  const guard = guardAt(phase);

  // The rear hand travels further and turns the body more, so its cycle is the
  // one with the bigger weight transfer.
  const power = arm === 'rear' ? 1 : 0.78;
  const drive = Math.max(0, reach) * power;

  const groundY = 300;
  const hipY = 206;
  const shoulderY = 128;

  // Weight transfer: the whole figure moves onto the front foot, drops a little
  // as the hips turn, and rises back on the recovery.
  const lean = drive * 15 - Math.min(0, reach) * 10;
  const drop = Math.sin(Math.max(0, reach) * Math.PI) * 6;
  const cx = 92 + lean;

  const hip = { x: cx, y: hipY + drop };
  const shoulder = { x: cx + drive * 7, y: shoulderY + drop * 0.7 };

  // --- legs -----------------------------------------------------------------
  // The rear leg straightens as the punch lands; in a still frame that is where
  // the power reads from.
  const rearFoot = { x: cx - 52 - drive * 8, y: groundY };
  const leadFoot = { x: cx + 36 + drive * 6, y: groundY };

  boot(g, rearFoot.x, groundY, PALETTE.boot);
  limb(
    g,
    { x: hip.x - 16, y: hip.y },
    { x: rearFoot.x + 10 + drive * 4, y: (hip.y + groundY) / 2 + 6 },
    { x: rearFoot.x, y: groundY - 12 },
    27,
    13,
    PALETTE.skinShade,
  );

  // --- torso ----------------------------------------------------------------
  torso(g, shoulder, hip, drive);
  trunks(g, hip, drive);

  // Lead leg drawn over the trunks, since it is the nearer of the two.
  limb(
    g,
    { x: hip.x + 14, y: hip.y },
    { x: leadFoot.x - 10, y: (hip.y + groundY) / 2 + 10 },
    { x: leadFoot.x, y: groundY - 12 },
    28,
    14,
    PALETTE.skin,
  );
  boot(g, leadFoot.x, groundY, PALETTE.bootLit);

  // --- arms and head --------------------------------------------------------
  // The far arm goes down before the head, the near arm after it, which is what
  // gives a flat silhouette any depth at all.
  const rearThrows = arm === 'rear';

  if (rearThrows) {
    drawArm(g, { x: shoulder.x - 18, y: shoulder.y + 4 }, reach, -1, PALETTE.skinShade, PALETTE.gloveShade, PALETTE.glove);
  } else {
    drawArm(g, { x: shoulder.x - 18, y: shoulder.y + 4 }, guard, -1, PALETTE.skinShade, PALETTE.gloveShade, PALETTE.glove);
  }

  head(g, shoulder, drive);

  if (rearThrows) {
    drawArm(g, { x: shoulder.x + 16, y: shoulder.y }, guard, 1, PALETTE.skin, PALETTE.glove, PALETTE.gloveLit);
  } else {
    drawArm(g, { x: shoulder.x + 16, y: shoulder.y }, reach, 1, PALETTE.skin, PALETTE.glove, PALETTE.gloveLit);
  }
}

/**
 * A tapered limb: two bezier edges from `from` through `via` to `to`, wide at
 * the start and narrow at the end.
 *
 * Drawing a limb as a thick stroke -- which is what this replaced -- gives it a
 * constant width and two circular caps, and an arm of constant width is the
 * single strongest tell that a figure was assembled from primitives.
 */
function limb(
  g: Graphics,
  from: Point,
  via: Point,
  to: Point,
  widthStart: number,
  widthEnd: number,
  color: number,
): void {
  const nStart = normal(from, via);
  const nEnd = normal(via, to);

  const a1 = offset(from, nStart, widthStart / 2);
  const a2 = offset(from, nStart, -widthStart / 2);
  const b1 = offset(to, nEnd, widthEnd / 2);
  const b2 = offset(to, nEnd, -widthEnd / 2);
  const mid = (widthStart + widthEnd) / 4;
  const v1 = offset(via, nStart, mid);
  const v2 = offset(via, nStart, -mid);

  // The tip bulges along the direction of travel, so a wrist or an ankle closes
  // off in a curve rather than being cut square across.
  const heading = direction(via, to);
  const tip = offset(to, heading, widthEnd * 0.6);

  g.moveTo(a1.x, a1.y)
    .quadraticCurveTo(v1.x, v1.y, b1.x, b1.y)
    .quadraticCurveTo(tip.x, tip.y, b2.x, b2.y)
    .quadraticCurveTo(v2.x, v2.y, a2.x, a2.y)
    .closePath()
    .fill(color);
}

/**
 * The torso, as one closed bezier outline.
 *
 * Wide at the shoulders, drawn in at the waist, and flared again at the hips,
 * with the near side lit and the far side in shade. The turn of the hips is
 * carried by `drive`, which pulls the far shoulder round.
 */
function torso(g: Graphics, shoulder: Point, hip: Point, drive: number): void {
  const shoulderHalf = 30 - drive * 4;
  const waistHalf = 21;
  const hipHalf = 25;
  const waistY = (shoulder.y + hip.y) / 2 + 6;

  const leftShoulder = { x: shoulder.x - shoulderHalf, y: shoulder.y };
  const rightShoulder = { x: shoulder.x + shoulderHalf, y: shoulder.y };

  g.moveTo(leftShoulder.x, leftShoulder.y)
    // Far side: shoulder, lat, hip.
    .quadraticCurveTo(shoulder.x - shoulderHalf - 4, waistY - 18, hip.x - waistHalf, waistY)
    .quadraticCurveTo(hip.x - hipHalf, hip.y - 14, hip.x - hipHalf + 3, hip.y + 6)
    // Across the hips.
    .lineTo(hip.x + hipHalf - 3, hip.y + 6)
    // Near side coming back up.
    .quadraticCurveTo(hip.x + hipHalf, hip.y - 14, hip.x + waistHalf, waistY)
    .quadraticCurveTo(shoulder.x + shoulderHalf + 4, waistY - 18, rightShoulder.x, rightShoulder.y)
    // The line of the collarbones, bowed rather than straight.
    .quadraticCurveTo(shoulder.x, shoulder.y - 12, leftShoulder.x, leftShoulder.y)
    .closePath()
    .fill(PALETTE.skin);

  // Lit edge down the near side of the ribs.
  g.moveTo(rightShoulder.x - 5, rightShoulder.y + 4)
    .quadraticCurveTo(shoulder.x + shoulderHalf - 2, waistY - 16, hip.x + waistHalf - 5, waistY + 2)
    .quadraticCurveTo(hip.x + hipHalf - 6, hip.y - 12, hip.x + hipHalf - 9, hip.y + 4)
    .lineTo(hip.x + waistHalf - 14, hip.y + 4)
    .quadraticCurveTo(hip.x + 8, waistY, rightShoulder.x - 16, rightShoulder.y + 6)
    .closePath()
    .fill({ color: PALETTE.skinLit, alpha: 0.55 });

  // Pectoral shadow and the centre line, which is what stops the chest reading
  // as a flat panel.
  g.moveTo(shoulder.x - 16, shoulder.y + 16)
    .quadraticCurveTo(shoulder.x, shoulder.y + 30, shoulder.x + 16, shoulder.y + 14)
    .stroke({ width: 3, color: PALETTE.skinShade, alpha: 0.5, cap: 'round' });
  g.moveTo(shoulder.x + 1, shoulder.y + 26)
    .lineTo(hip.x + 3, waistY + 4)
    .stroke({ width: 2.5, color: PALETTE.skinShade, alpha: 0.35, cap: 'round' });
}

function trunks(g: Graphics, hip: Point, drive: number): void {
  const half = 28;
  const top = hip.y - 12;
  const bottom = hip.y + 34;

  g.moveTo(hip.x - half, top)
    .lineTo(hip.x + half, top)
    // The legs of the trunks, cut away at the inside of each thigh.
    .quadraticCurveTo(hip.x + half + 2, bottom - 6, hip.x + half - 6, bottom)
    .quadraticCurveTo(hip.x + 12, bottom - 12, hip.x + 2, bottom - 16)
    .quadraticCurveTo(hip.x - 12, bottom - 12, hip.x - half + 6, bottom)
    .quadraticCurveTo(hip.x - half - 2, bottom - 6, hip.x - half, top)
    .closePath()
    .fill(PALETTE.trunks);

  // Near-side sheen, shifted with the hips so the cloth turns with the body.
  g.moveTo(hip.x + 4 + drive * 3, top)
    .quadraticCurveTo(hip.x + half, top + 18, hip.x + half - 7, bottom - 2)
    .quadraticCurveTo(hip.x + 16, bottom - 10, hip.x + 10 + drive * 3, top + 6)
    .closePath()
    .fill({ color: PALETTE.trunksLit, alpha: 0.7 });

  // Waistband.
  g.moveTo(hip.x - half, top + 1)
    .lineTo(hip.x + half, top + 1)
    .stroke({ width: 8, color: PALETTE.trunksTrim, cap: 'butt' });
}

/**
 * The head: skull, jaw and hair as three curves rather than three circles.
 *
 * The chin tucks behind the lead shoulder as the punch lands, which is both what
 * a boxer does and what keeps the head from floating free of the body.
 */
function head(g: Graphics, shoulder: Point, drive: number): void {
  const cxHead = shoulder.x + 6 + drive * 6;
  const cyHead = shoulder.y - 30 + drive * 5;

  // Neck, behind the head.
  limb(
    g,
    { x: shoulder.x - 2, y: shoulder.y + 4 },
    { x: cxHead - 3, y: cyHead + 18 },
    { x: cxHead - 2, y: cyHead + 10 },
    20,
    16,
    PALETTE.skinShade,
  );

  // Skull and jaw in one outline: round at the crown, tapering to the chin.
  g.moveTo(cxHead - 19, cyHead - 2)
    .quadraticCurveTo(cxHead - 19, cyHead - 24, cxHead + 2, cyHead - 24)
    .quadraticCurveTo(cxHead + 20, cyHead - 23, cxHead + 21, cyHead - 3)
    .quadraticCurveTo(cxHead + 21, cyHead + 12, cxHead + 8, cyHead + 20)
    .quadraticCurveTo(cxHead - 6, cyHead + 24, cxHead - 15, cyHead + 12)
    .quadraticCurveTo(cxHead - 19, cyHead + 6, cxHead - 19, cyHead - 2)
    .closePath()
    .fill(PALETTE.skin);

  // Cheekbone highlight on the near side.
  g.moveTo(cxHead + 6, cyHead - 12)
    .quadraticCurveTo(cxHead + 18, cyHead - 6, cxHead + 10, cyHead + 10)
    .quadraticCurveTo(cxHead + 4, cyHead + 2, cxHead + 6, cyHead - 12)
    .closePath()
    .fill({ color: PALETTE.skinLit, alpha: 0.5 });

  // Hair, sitting over the crown and down the back of the skull.
  g.moveTo(cxHead - 20, cyHead - 4)
    .quadraticCurveTo(cxHead - 22, cyHead - 27, cxHead + 3, cyHead - 27)
    .quadraticCurveTo(cxHead + 19, cyHead - 26, cxHead + 18, cyHead - 14)
    .quadraticCurveTo(cxHead + 6, cyHead - 20, cxHead - 8, cyHead - 14)
    .quadraticCurveTo(cxHead - 15, cyHead - 10, cxHead - 14, cyHead + 2)
    .closePath()
    .fill(PALETTE.hair);

  // Brow and the shadow under the jaw: two strokes, and the difference between
  // a head and an oval.
  g.moveTo(cxHead + 2, cyHead - 8)
    .quadraticCurveTo(cxHead + 12, cyHead - 10, cxHead + 17, cyHead - 6)
    .stroke({ width: 3, color: PALETTE.skinDeep, alpha: 0.6, cap: 'round' });
  g.moveTo(cxHead - 12, cyHead + 12)
    .quadraticCurveTo(cxHead - 2, cyHead + 22, cxHead + 9, cyHead + 17)
    .stroke({ width: 3, color: PALETTE.skinDeep, alpha: 0.45, cap: 'round' });
}

/**
 * One arm, from the shoulder to the glove.
 *
 * `extend` is negative at the top of the wind-up, 0 for a tucked guard and 1 for
 * full extension. `depth` shifts the far arm so the two do not merge into one
 * shape.
 */
function drawArm(
  g: Graphics,
  shoulder: Point,
  extend: number,
  depth: 1 | -1,
  limbColor: number,
  gloveColor: number,
  gloveLit: number,
): void {
  // Three poses the arm is interpolated between, rather than two: a punch that
  // travels straight from guard to extension has no elbow in it.
  //
  // The guard glove sits *beside* the head rather than in front of it. Tucked in
  // over the cheek is what a boxer actually does, but on a flat silhouette the
  // two gloves then land on top of the face and the figure reads as a pair of
  // red blobs with no head at all -- so they are held wide enough to frame it.
  const cocked = {
    elbow: { x: shoulder.x - 4, y: shoulder.y + 30 },
    glove: { x: shoulder.x - 20, y: shoulder.y + 8 },
  };
  const guard = {
    elbow: { x: shoulder.x + 17 * depth, y: shoulder.y + 30 },
    glove: { x: shoulder.x + 27 * depth, y: shoulder.y - 6 },
  };
  const punch = {
    elbow: { x: shoulder.x + 46, y: shoulder.y + 6 },
    glove: { x: shoulder.x + 100, y: shoulder.y - 4 },
  };

  let elbow: Point;
  let glove: Point;
  if (extend < 0) {
    const t = Math.min(1, -extend / 0.2);
    elbow = lerpPoint(guard.elbow, cocked.elbow, t);
    glove = lerpPoint(guard.glove, cocked.glove, t);
  } else {
    elbow = lerpPoint(guard.elbow, punch.elbow, extend);
    glove = lerpPoint(guard.glove, punch.glove, extend);
  }

  // Upper arm and forearm as two tapered shapes, so the elbow is a joint rather
  // than a bend in a pipe.
  limb(g, shoulder, midpoint(shoulder, elbow), elbow, 23, 16, limbColor);
  limb(g, elbow, midpoint(elbow, glove), glove, 16, 13, limbColor);

  // Smaller than the head, and smaller still at full extension where it is
  // furthest from the eye.
  const radius = 16 - Math.max(0, extend) * 2.5;

  // The glove: a rounded mass with the thumb bulging on the inside and a lace
  // seam across the knuckles.
  g.moveTo(glove.x - radius, glove.y)
    .quadraticCurveTo(glove.x - radius, glove.y - radius * 1.1, glove.x + radius * 0.2, glove.y - radius)
    .quadraticCurveTo(glove.x + radius * 1.15, glove.y - radius * 0.8, glove.x + radius * 1.05, glove.y + radius * 0.25)
    .quadraticCurveTo(glove.x + radius * 0.9, glove.y + radius, glove.x - radius * 0.1, glove.y + radius)
    .quadraticCurveTo(glove.x - radius * 0.9, glove.y + radius * 0.9, glove.x - radius, glove.y)
    .closePath()
    .fill(gloveColor);

  // Knuckle highlight, on the leading face.
  g.moveTo(glove.x + radius * 0.15, glove.y - radius * 0.85)
    .quadraticCurveTo(glove.x + radius, glove.y - radius * 0.6, glove.x + radius * 0.85, glove.y + radius * 0.1)
    .quadraticCurveTo(glove.x + radius * 0.3, glove.y - radius * 0.2, glove.x + radius * 0.15, glove.y - radius * 0.85)
    .closePath()
    .fill({ color: gloveLit, alpha: 0.85 });

  // Wrist wrap.
  g.moveTo(glove.x - radius * 0.95, glove.y + radius * 0.15)
    .quadraticCurveTo(glove.x - radius * 0.5, glove.y + radius * 0.5, glove.x - radius * 0.1, glove.y + radius * 0.2)
    .stroke({ width: 4, color: PALETTE.gloveShade, alpha: 0.8, cap: 'round' });
}

function boot(g: Graphics, x: number, groundY: number, color: number): void {
  g.moveTo(x - 18, groundY - 18)
    .quadraticCurveTo(x - 22, groundY - 2, x - 14, groundY)
    .lineTo(x + 18, groundY)
    .quadraticCurveTo(x + 25, groundY - 3, x + 20, groundY - 15)
    .quadraticCurveTo(x + 2, groundY - 23, x - 18, groundY - 18)
    .closePath()
    .fill(color);
  // Sole.
  g.moveTo(x - 15, groundY - 1).lineTo(x + 18, groundY - 1)
    .stroke({ width: 3, color: PALETTE.floorLine, alpha: 0.9, cap: 'round' });
}

// ---------------------------------------------------------------------------
// The bag and the debris
// ---------------------------------------------------------------------------

function drawBag(g: Graphics): void {
  const top = 34;
  const bottom = 292;
  const half = 48;
  const cx = 75;

  // Chain up to the ceiling mount, and the swivel it hangs from.
  g.moveTo(cx, 0).lineTo(cx, top - 12).stroke({ width: 6, color: PALETTE.strap, cap: 'round' });
  g.circle(cx, top - 8, 6).fill(PALETTE.strapLit);

  // Straps over the crown.
  g.moveTo(cx - half + 12, top + 14)
    .quadraticCurveTo(cx, top - 8, cx + half - 12, top + 14)
    .stroke({ width: 7, color: PALETTE.strap, cap: 'round', join: 'round' });

  // The body: a cylinder with a domed top and a rounded base, in one outline
  // rather than a rounded rectangle. A bag is fatter in the middle than at
  // either end, and that curve is what a swing reads against.
  g.moveTo(cx - half, top + 34)
    .quadraticCurveTo(cx - half + 4, top + 2, cx, top)
    .quadraticCurveTo(cx + half - 4, top + 2, cx + half, top + 34)
    .quadraticCurveTo(cx + half + 5, (top + bottom) / 2, cx + half - 2, bottom - 30)
    .quadraticCurveTo(cx + half - 6, bottom, cx, bottom)
    .quadraticCurveTo(cx - half + 6, bottom, cx - half + 2, bottom - 30)
    .quadraticCurveTo(cx - half - 5, (top + bottom) / 2, cx - half, top + 34)
    .closePath()
    .fill(PALETTE.bagBody);

  // Lit band down the near side and shade on the far, both following the
  // barrel's curve so the bag reads as round.
  g.moveTo(cx - half + 8, top + 36)
    .quadraticCurveTo(cx - half + 12, top + 10, cx - 10, top + 6)
    .quadraticCurveTo(cx - 22, (top + bottom) / 2, cx - 20, bottom - 12)
    .quadraticCurveTo(cx - half + 12, bottom - 20, cx - half + 8, top + 36)
    .closePath()
    .fill({ color: PALETTE.bagHighlight, alpha: 0.85 });

  g.moveTo(cx + half - 6, top + 36)
    .quadraticCurveTo(cx + half - 2, (top + bottom) / 2, cx + half - 6, bottom - 26)
    .quadraticCurveTo(cx + 16, bottom - 14, cx + 20, top + 20)
    .quadraticCurveTo(cx + half - 10, top + 14, cx + half - 6, top + 36)
    .closePath()
    .fill({ color: PALETTE.bagShade, alpha: 0.75 });

  // Stitched seams, bowed with the surface.
  for (const y of [top + 62, top + 146, top + 224]) {
    g.moveTo(cx - half + 6, y)
      .quadraticCurveTo(cx, y + 7, cx + half - 6, y)
      .stroke({ width: 3, color: PALETTE.bagShade, alpha: 0.65 });
  }
}

/** A torn chunk: canvas on one side, stuffing on the other. */
function drawChunk(g: Graphics): void {
  // Tinted per particle at spawn, so this is drawn white and shaded by alpha.
  g.moveTo(2, 5)
    .quadraticCurveTo(7, 1, 14, 3)
    .quadraticCurveTo(17, 9, 13, 15)
    .quadraticCurveTo(6, 17, 2, 12)
    .closePath()
    .fill(0xffffff);
  g.moveTo(4, 6).quadraticCurveTo(9, 4, 13, 7).stroke({ width: 1.5, color: 0x000000, alpha: 0.25 });
}

/**
 * A grain of sand: soft-edged, because a hard-edged square at this size is what
 * makes a particle plume look like confetti.
 */
function drawSandGrain(g: Graphics): void {
  g.circle(6, 6, 5).fill({ color: 0xffffff, alpha: 0.28 });
  g.circle(6, 6, 3.2).fill({ color: 0xffffff, alpha: 0.75 });
  g.circle(5.4, 5.4, 1.6).fill(0xffffff);
}

function drawFloor(g: Graphics): void {
  g.rect(0, 0, 512, 96).fill(PALETTE.floor);
  g.moveTo(0, 1).lineTo(512, 1).stroke({ width: 2, color: PALETTE.floorLine });
  // Sparse boards, purely to give the shake something to read against.
  for (let x = 0; x < 512; x += 64) {
    g.moveTo(x, 0).lineTo(x - 18, 96).stroke({ width: 1, color: PALETTE.floorLine, alpha: 0.6 });
  }
}

// ---------------------------------------------------------------------------

interface Point {
  x: number;
  y: number;
}

function lerpPoint(a: Point, b: Point, t: number): Point {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** The unit normal of a->b, for offsetting a limb's two edges. */
function normal(a: Point, b: Point): Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy) || 1;
  return { x: -dy / length, y: dx / length };
}

/** The unit vector from a to b. */
function direction(a: Point, b: Point): Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy) || 1;
  return { x: dx / length, y: dy / length };
}

function offset(p: Point, n: Point, distance: number): Point {
  return { x: p.x + n.x * distance, y: p.y + n.y * distance };
}
